import { Button , Card, CardContent, CardHeader, CardTitle } from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Bytes } from "@/components/bytes";
import { Download, RefreshCw } from "@/components/icons";
import { RelativeTime } from "@/components/relative-time";
import { StatusBadge } from "@/components/status-badge";
import { createZip, findZip } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import type { ZipStatusDoc } from "@/lib/types";

interface Props {
  siteId: string;
  zipStatus: ZipStatusDoc;
}

export function ZipStatusCard({ siteId, zipStatus }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pollRef = useRef<number | null>(null);

  const build = useMutation({
    mutationFn: () => createZip({ siteId }),
    onSuccess: () => {
      toast.success(t("zip.buildStarted"));
      void queryClient.invalidateQueries({ queryKey: ["site", siteId] });
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  useEffect(() => {
    const stopPolling = () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    if (zipStatus.status !== "refreshing") {
      stopPolling();
      return;
    }
    if (pollRef.current !== null) return;
    pollRef.current = window.setInterval(async () => {
      try {
        const next = await findZip({ siteId });
        if (next.status !== "refreshing") {
          stopPolling();
          await queryClient.invalidateQueries({ queryKey: ["site", siteId] });
          if (next.status === "ready") toast.success(t("zip.ready"));
          if (next.status === "failed") toast.error(next.message ?? t("zip.buildFailed"));
        }
      } catch (err) {
        stopPolling();
        toast.error(toAppError(err).message);
      }
    }, 4000);
    return stopPolling;
  }, [zipStatus.status, siteId, queryClient, t]);

  const dateCreated = zipStatus.dateCreated ? new Date(zipStatus.dateCreated) : null;
  const isReady = zipStatus.status === "ready";
  const isRefreshing = zipStatus.status === "refreshing";
  const isFailed = zipStatus.status === "failed";
  const isEmpty = zipStatus.status === "none";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold">{t("zip.title")}</CardTitle>
        <StatusBadge status={zipStatus.status} />
      </CardHeader>
      <CardContent className="space-y-4">
        {isEmpty && (
          <p className="text-sm text-muted-foreground">
            <Trans i18nKey="zip.empty" components={{ mono: <span className="font-mono" /> }} />
          </p>
        )}

        {isRefreshing && (
          <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" /> {t("zip.preparing")}
          </p>
        )}

        {isFailed && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {zipStatus.message ?? t("zip.lastBuildFailed")}
          </div>
        )}

        {isReady && (
          <div className="space-y-3 rounded-md border bg-muted/30 p-3">
            <p className="truncate font-mono text-sm" title={zipStatus.fileName ?? undefined}>
              {zipStatus.fileName ?? "archive.zip"}
            </p>
            <dl className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">{t("zip.files")}</dt>
                <dd>{zipStatus.fileCount ?? 0}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">{t("zip.size")}</dt>
                <dd>{zipStatus.sizeBytes ? <Bytes value={zipStatus.sizeBytes} /> : "-"}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">{t("zip.built")}</dt>
                <dd>
                  <RelativeTime date={dateCreated} />
                </dd>
              </div>
            </dl>
          </div>
        )}

        <div className="space-y-2">
          {isReady && zipStatus.downloadUrl && (
            <Button asChild className="w-full">
              <a href={zipStatus.downloadUrl} target="_blank" rel="noreferrer">
                <Download /> {t("zip.download")}
              </a>
            </Button>
          )}
          <Button
            className="w-full"
            variant={isReady ? "outline" : "default"}
            disabled={isRefreshing || build.isPending}
            onClick={() => build.mutate()}
          >
            <RefreshCw />
            {isReady || isFailed ? t("zip.rebuild") : t("zip.build")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
